import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { Plus, CalendarHeart, Trash2, RefreshCw } from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import RowActionsMenu from "@/components/RowActionsMenu";
import Banner from "@/components/Banner";
import { hasAction, hasStaffAccess, isStrictAdmin } from "@/lib/rbac";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const TYPE_COLORS = {
  Public: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Restricted: "bg-amber-100 text-amber-700 border-amber-200",
  Optional: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Holidays() {
  const { user } = useAuth();
  const canManage = isStrictAdmin(user) || hasAction(user, "holidays", "add") || hasAction(user, "holidays", "edit");
  const canDelete = isStrictAdmin(user) || hasAction(user, "holidays", "delete");
  const crumbs = hasStaffAccess(user)
    ? ["Home", "Organization", "Holiday Calendar"]
    : ["Home", "Self Service", "Holiday Calendar"];
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", type: "Public", description: "" });
  const [err, setErr] = useState("");

  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(items, { initialKey: "date" });

  const load = () =>
    api.get("/holidays", { params: { year } }).then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/holidays", form);
      setOpen(false);
      setForm({ name: "", date: "", type: "Public", description: "" });
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this holiday?")) return;
    await api.delete(`/holidays/${id}`);
    load();
  };

  return (
    <AdminLayout title="Holiday Calendar" breadcrumbs={crumbs}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <CalendarHeart className="h-5 w-5 text-[#226DB4]" /> Holidays {year}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              title="Refresh"
              data-testid="holidays-refresh-btn"
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white"
              data-testid="holiday-year-select"
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {canManage && (
              <button
                data-testid="add-holiday-btn"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded font-medium"
              >
                <Plus className="h-4 w-4" /> Add Holiday
              </button>
            )}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortableTH columnKey="date" active={sortBy} dir={sortDir} onClick={requestSort}>Date</SortableTH>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Day</th>
              <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort}>Name</SortableTH>
              <SortableTH columnKey="type" active={sortBy} dir={sortDir} onClick={requestSort}>Type</SortableTH>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Notes</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <EmptyState colSpan={6} message={`No holidays defined for ${year}`} />
            ) : (
              sortedItems.map((h) => {
                const day = new Date(h.date).toLocaleDateString("en-IN", { weekday: "long" });
                return (
                  <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-slate-800">{formatDisplayDate(h.date)}</td>
                    <td className="px-4 py-3 text-slate-600">{day}</td>
                    <td className="px-4 py-3 text-slate-800">{h.name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-1 rounded border font-semibold ${TYPE_COLORS[h.type] || ""}`}>
                        {h.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{h.description || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <RowActionsMenu
                        testid={`holiday-actions-${h.id}`}
                        items={[
                          { label: "Delete", icon: Trash2, danger: true, onClick: () => onDelete(h.id), hidden: !canDelete, testid: `delete-holiday-${h.id}` },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">New Holiday</h3>
            <input required placeholder="Holiday Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="holiday-name-input" />
            <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="holiday-date-input" />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
              <option>Public</option>
              <option>Restricted</option>
              <option>Optional</option>
            </select>
            <textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
            <Banner variant="error">{err}</Banner>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded" data-testid="holiday-save-btn">Save</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
